import { Component, forwardRef } from 'react';
import { DefaultTheme, ThemeProps, withTheme } from 'styled-components';
import { getSpacingToken } from '@optimus-web/common';
import {
    ActivableRenderer,
    ActivableRendererProps,
    Positioner,
    PositionerDefaultedProps,
    PositioningStrategy,
} from '@optimus-web/helpers';
import { DropdownFooter, FooterProps } from './DropdownFooter';
import { StyledPositioner, StyledPositionerProps } from './style';

export type OpenableOverlayProps = StyledPositionerProps &
    PositionerDefaultedProps &
    ActivableRendererProps &
    ThemeProps<DefaultTheme> & {
        footerProps?: FooterProps;
    };

export type IOpenableOverlay = Pick<Positioner, 'updatePlacement'>;

const getOpenableOverlayDefaultProps = (theme: DefaultTheme): Partial<OpenableOverlayProps> => ({
    sizeToAnchor: false,
    scrollOnOverflow: true,
    maxHeight: 500,
    minWidth: 16,
    offset: getSpacingToken('100')({ theme }),
    placement: 'bottom',
    positioningStrategy: PositioningStrategy.Fixed,
});

interface VisiblePositionerProps extends StyledPositionerProps, PositionerDefaultedProps {
    forwardedRef?: React.Ref<Positioner>;
}

class VisiblePositioner extends Component<VisiblePositionerProps> {
    shouldComponentUpdate(nextProps: VisiblePositionerProps) {
        if (nextProps.visible === false && this.props.visible === false) {
            return false;
        }

        return true;
    }

    render() {
        const { forwardedRef, ...other } = this.props;

        return <StyledPositioner ref={forwardedRef} {...other} />;
    }
}

/**
 * ActivableRenderer now controls the mounting/unmounting of the component, so we no longer need Positioner to do that, therefore
 * we can just set opened={true} at all times. However, we still want to have animation, so Positioner is extended with the
 * "visible" prop (which controls opacity) that is managed by ActivableRenderer.
 */
const OpenableOverlay = ActivableRenderer<Positioner, OpenableOverlayProps>({ activePropName: 'opened' })(
    forwardRef<Positioner, OpenableOverlayProps>(({ opened, theme, children, footerProps = {}, ...other }, ref) => {
        const { close = () => {}, primary, secondary, listRef, virtualized, footerRef, isMobile } = footerProps;

        return (
            <VisiblePositioner
                opened
                visible={opened}
                forwardedRef={ref}
                {...getOpenableOverlayDefaultProps(theme)}
                {...other}
            >
                <>
                    {children}
                    {primary || secondary ? (
                        <DropdownFooter
                            primary={primary}
                            secondary={secondary}
                            close={close}
                            listRef={listRef}
                            virtualized={virtualized}
                            ref={footerRef}
                            isMobile={isMobile}
                        />
                    ) : null}
                </>
            </VisiblePositioner>
        );
    })
);

export default withTheme(OpenableOverlay);
